import { useState } from 'react';
import {
  DeleteButton,
  Empty,
  EmployeePicker,
  ErrorBanner,
  Field,
  Loading,
  Modal,
  PageHead,
} from '../components/ui';
import { api } from '../lib/api';
import { assessmentLabel, formatDate, humanise } from '../lib/format';
import { useResource } from '../lib/hooks';
import type {
  AssessmentSummaryRow,
  ComplianceCategory,
  ComplianceFamily,
  ComplianceFramework,
  ComplianceRequirementName,
  ComplianceSubject,
  ComplianceSummary,
  Employee,
} from '../lib/types';
import { AssessmentWorkspace } from './ComplianceAssessment';

/**
 * What each category of obligation actually means.
 *
 * This is the part that is worth writing down. "Compliance" gets used as one
 * word for five different things, and the difference between them is the
 * difference between a fine, a lost contract, a declined renewal and a good
 * idea nobody got round to.
 */
/* Ordered deliberately: what can be enforced against you first, what you
   chose for yourself last. */
const CATEGORIES: { key: ComplianceCategory; title: string; blurb: string }[] = [
  {
    key: 'regulatory',
    title: 'Regulatory',
    blurb:
      'Binding by law. A regulator can act against you for missing these, and “we did not know it applied to us” is not a defence.',
  },
  {
    key: 'contractual',
    title: 'Contractual',
    blurb:
      'Binding by contract. Nothing applies until you sign — and then it flows down to you, and from you to your subcontractors.',
  },
  {
    key: 'insurance',
    title: 'Insurance',
    blurb:
      'Not a standard at all — a carrier’s conditions for writing or renewing a policy. Missing one of the core controls is what gets a renewal declined or priced to hurt.',
  },
  {
    key: 'internal',
    title: 'Internal',
    blurb:
      'Our own standard. It should sit above everything else here, or it is not worth having.',
  },
  {
    key: 'voluntary',
    title: 'Voluntary',
    blurb:
      'Nobody makes you. Adopted because it is the cheapest route to being defensible when something does go wrong.',
  },
];

/**
 * "Our own organisation" as a subject.
 *
 * An assessment stores its subject as `ClientId`, where NULL means us. NULL is
 * therefore a meaningful value rather than "nothing selected", so the client
 * views need a separate sentinel to say "no subject open" without colliding
 * with it.
 */
const OURSELVES = 'ourselves' as const;

/**
 * Compliance.
 *
 * A catalogue of the frameworks, what each one is, and what this business has
 * decided about it. It deliberately stops short of scoring individual
 * requirements — the Compliance Tracker already does that, control by control
 * and client by client, and two systems answering "are we compliant" is worse
 * than one.
 */
export function CompliancePage() {
  const frameworks = useResource<ComplianceFramework[]>('/compliance/frameworks');
  const summary = useResource<ComplianceSummary>('/compliance/summary');
  const assessments = useResource<AssessmentSummaryRow[]>('/compliance/assessments');
  const subjects = useResource<ComplianceSubject[]>('/compliance/subjects');
  const employees = useResource<Employee[]>('/employees?status=active');

  const [tab, setTab] = useState<'clients' | 'catalogue' | 'assessments'>('catalogue');
  const [selected, setSelected] = useState<string | null>(null);
  /* A subject id, or the OURSELVES sentinel. Not just `number | null`, because
     null is already a real subject here — our own organisation. */
  const [openClient, setOpenClient] = useState<number | typeof OURSELVES | null>(null);
  const [openAssessment, setOpenAssessment] = useState<number | null>(null);
  const [starting, setStarting] = useState<ComplianceFramework | 'any' | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (frameworks.loading) return <Loading label="Loading the catalogue" />;
  if (frameworks.error) return <ErrorBanner message={frameworks.error} />;

  const all = frameworks.data ?? [];
  const s = summary.data;
  const current = all.find((f) => f.Code === selected) ?? null;
  const rows = assessments.data ?? [];

  const reload = () => {
    frameworks.reload();
    summary.reload();
    assessments.reload();
    /* An organisation added from the start-assessment dialog has to show up in
       the picker next time it opens. */
    subjects.reload();
  };

  /* Working an assessment takes the whole page — a 153-safeguard worklist in a
     panel beside something else is a worklist nobody finishes. */
  if (openAssessment !== null) {
    return (
      <AssessmentWorkspace
        assessmentId={openAssessment}
        onBack={() => {
          setOpenAssessment(null);
          assessments.reload();
        }}
        onChanged={() => assessments.reload()}
      />
    );
  }

  const startModal = starting && (
    <NewAssessment
      frameworks={all}
      subjects={subjects.data ?? []}
      employees={employees.data ?? []}
      preselect={starting === 'any' ? null : starting}
      existing={rows}
      onClose={() => setStarting(null)}
      onFrameworkSaved={(id, patch) => {
        /* Patch the row in place and refresh only the summary. Reloading the
           whole list would swap the array under the open dialog. */
        frameworks.set((cur) => cur.map((f) => (f.FrameworkId === id ? { ...f, ...patch } : f)));
        summary.reload();
      }}
      onOpenExisting={(id) => {
        setStarting(null);
        setOpenAssessment(id);
      }}
      onCreated={(id) => {
        setStarting(null);
        reload();
        setOpenAssessment(id);
      }}
    />
  );

  if (openClient !== null) {
    const subject =
      openClient === OURSELVES ? null : (subjects.data ?? []).find((s) => s.ClientId === openClient);
    return (
      <>
        <ClientDetail
          clientId={openClient === OURSELVES ? null : openClient}
          name={openClient === OURSELVES ? 'Our organisation' : (subject?.Name ?? 'Unknown')}
          subjectStatus={openClient === OURSELVES ? null : (subject?.Status ?? null)}
          assessments={rows.filter((a) =>
            openClient === OURSELVES ? a.ClientId === null : a.ClientId === openClient,
          )}
          onBack={() => setOpenClient(null)}
          onOpenAssessment={setOpenAssessment}
          onStart={() => setStarting('any')}
          onSaved={() => summary.reload()}
          onError={setError}
        />
        {startModal}
      </>
    );
  }

  if (current) {
    return (
      <>
        <FrameworkDetail
          framework={current}
          assessments={rows.filter((a) => a.FrameworkId === current.FrameworkId)}
          onBack={() => setSelected(null)}
          onAssess={() => setStarting(current)}
          onOpenAssessment={setOpenAssessment}
        />
        {startModal}
      </>
    );
  }

  return (
    <>
      <PageHead
        title="Compliance"
        lede="Every framework we could be held to, what it actually is, and what we have decided about it. Five different kinds of obligation get called “compliance”; the difference between them is the difference between a fine, a lost contract and a good idea."
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setStarting('any')}>
            Start an assessment
          </button>
        }
      />

      {error && <ErrorBanner message={error} />}

      {s && (
        <div className="grid grid-4 mb-4">
          {/* Counted in subject × framework pairs now, not frameworks: "CMMC
              for Northwind" is a different obligation from "CMMC for us". */}
          <div className="stat">
            <div className="label">In scope</div>
            <div className="figure">{s.InScope}</div>
            <div className="foot">
              obligations across {s.Subjects} {s.Subjects === 1 ? 'organisation' : 'organisations'} ·{' '}
              {s.Required} required
            </div>
          </div>
          <div className="stat">
            <div className="label">Maintained</div>
            <div className="figure">{s.Maintained}</div>
            <div className="foot">
              {s.InScope === 0 ? 'nothing in scope yet' : `of ${s.InScope} in scope`}
            </div>
          </div>
          <div className="stat">
            <div className="label">Unowned</div>
            <div className={`figure${s.Unowned > 0 ? ' accent' : ''}`}>{s.Unowned}</div>
            <div className="foot">in scope, nobody named</div>
          </div>
          <div className="stat">
            <div className="label">Reviews overdue</div>
            <div className={`figure${s.ReviewsOverdue > 0 ? ' accent' : ''}`}>
              {s.ReviewsOverdue}
            </div>
            <div className="foot">
              {s.ReviewsDueSoon > 0 ? `${s.ReviewsDueSoon} due in 30 days` : 'none due in 30 days'}
            </div>
          </div>
        </div>
      )}

      <div className="tabs">
        <button
          type="button"
          className={`tab${tab === 'clients' ? ' active' : ''}`}
          onClick={() => setTab('clients')}
        >
          Clients
        </button>
        <button
          type="button"
          className={`tab${tab === 'catalogue' ? ' active' : ''}`}
          onClick={() => setTab('catalogue')}
        >
          Catalogue
        </button>
        <button
          type="button"
          className={`tab${tab === 'assessments' ? ' active' : ''}`}
          onClick={() => setTab('assessments')}
        >
          Assessments ({rows.length})
        </button>
      </div>

      {tab === 'clients' && (
        <ClientList
          subjects={subjects.data ?? []}
          assessments={rows}
          loading={subjects.loading}
          onOpen={setOpenClient}
        />
      )}

      {tab === 'assessments' && (
        <AssessmentList
          rows={rows}
          loading={assessments.loading}
          onOpen={setOpenAssessment}
          onStart={() => setStarting('any')}
          onDelete={(id) =>
            void api
              .del(`/compliance/assessments/${id}`)
              .then(reload)
              .catch((err: unknown) =>
                setError(err instanceof Error ? err.message : 'Could not delete'),
              )
          }
        />
      )}

      {/* Conditional rather than `hidden`: `.stack` sets display:flex, and an
          author rule beats the user-agent [hidden] rule, so the attribute
          alone leaves the catalogue on screen under the other tab. */}
      <div className="stack">
        {tab === 'catalogue' &&
          CATEGORIES.map((cat) => {
            const items = all.filter((f) => f.Category === cat.key);
            if (items.length === 0) return null;

            return (
              <section key={cat.key} className="card">
              <div className="card-head">
                <div className="grow">
                  <span className="eyebrow">{cat.title}</span>
                  <h2>
                    {items.length} {items.length === 1 ? 'framework' : 'frameworks'}
                  </h2>
                  <p className="sub">{cat.blurb}</p>
                </div>
              </div>

              <div className="card-body">
                <div className="framework-grid">
                  {items.map((f) => (
                    <button
                      key={f.Code}
                      type="button"
                      className={`framework${f.IsInScope ? ' in-scope' : ''}`}
                      onClick={() => setSelected(f.Code)}
                    >
                      <span className="framework-head">
                        <span className="framework-name">{f.Name}</span>
                      </span>

                      {f.Summary && <span className="framework-summary">{f.Summary}</span>}

                      {/* No applicability or status pill. The card describes
                          what the framework IS; our position on it is recorded
                          per client and read there. */}
                      <span className="framework-meta">
                        <span className="mono xs">{f.FamilyCount} families</span>
                        <span className="mono xs">{f.RequirementCount} requirements</span>
                        {f.ReviewOverdue && <span className="badge badge-critical">Review overdue</span>}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              </section>
            );
          })}
      </div>

      {startModal}
    </>
  );
}

/* =========================================================================
   One framework, in full
   ========================================================================= */

function FrameworkDetail({
  framework: f,
  assessments,
  onBack,
  onAssess,
  onOpenAssessment,
}: {
  framework: ComplianceFramework;
  assessments: AssessmentSummaryRow[];
  onBack: () => void;
  onAssess: () => void;
  onOpenAssessment: (id: number) => void;
}) {
  return (
    <>
      <button type="button" className="btn btn-ghost btn-sm mb-4" onClick={onBack}>
        ← All frameworks
      </button>

      <PageHead
        title={f.Name}
        lede={f.Summary ?? undefined}
        actions={
          <>
            {f.ReferenceUrl && (
              <a
                className="btn btn-ghost"
                href={f.ReferenceUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Source document ↗
              </a>
            )}
            <button type="button" className="btn btn-primary" onClick={onAssess}>
              Assess against it
            </button>
          </>
        }
      />

      {assessments.length > 0 && (
        <section className="card mb-4">
          <div className="card-head">
            <div>
              <span className="eyebrow">Scored</span>
              <h2>Assessments</h2>
            </div>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th style={{ width: 200 }}>Scope</th>
                  <th className="num" style={{ width: 90 }}>Met</th>
                  <th style={{ width: 150 }}>Progress</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {assessments.map((a) => (
                  <tr key={a.AssessmentId}>
                    <td className="strong">{a.ClientName ?? 'Our organisation'}</td>
                    <td className="xs muted">{a.ScopeLevelLabel ?? a.ScopeLevelKey}</td>
                    <td className="num mono">{a.ScorePct === null ? '—' : `${a.ScorePct}%`}</td>
                    <td>
                      <span className="xs muted">
                        {a.InScope - a.NotAssessed}/{a.InScope} assessed
                      </span>
                    </td>
                    <td className="actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => onOpenAssessment(a.AssessmentId)}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="mb-4">
        <section className="card">
          <div className="card-head">
            <div>
              <span className="eyebrow">{humanise(f.Category)}</span>
              <h2>What it is</h2>
            </div>
          </div>
          <div className="card-body">
            <dl className="facts">
              <dt>Who it applies to</dt>
              <dd>{f.AppliesTo ?? '—'}</dd>
              <dt>Set by</dt>
              <dd>{f.Authority ?? '—'}</dd>
              <dt>Assessed</dt>
              <dd>{assessmentLabel(f.AssessmentType)}</dd>
              <dt>Scope</dt>
              <dd>{f.ScopeLevels ?? '—'}</dd>
              <dt>Size</dt>
              <dd>
                <span className="mono">{f.FamilyCount}</span> families ·{' '}
                <span className="mono">{f.RequirementCount}</span> requirements
              </dd>
            </dl>
          </div>
        </section>

      </div>

      <section className="card">
        <div className="card-head">
          <div className="grow">
            <span className="eyebrow">Structure</span>
            <h2>Families</h2>
            <p className="sub">
              The domains the requirements hang off. Scoring each requirement is the Compliance
              Tracker&rsquo;s job — this is the shape of the thing.
            </p>
          </div>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 70 }}>Code</th>
                <th>Family</th>
                <th className="num" style={{ width: 130 }}>Requirements</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {f.families.map((fam) => (
                <FamilyRow key={fam.FamilyId} family={fam} />
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

/* =========================================================================
   Assessments
   ========================================================================= */

/**
 * A family in the framework's contents list, which opens to name its
 * requirements.
 *
 * Names only, and only when opened. This is the reading view — "what is
 * actually in this thing" — so it stops at the requirement title. Scoring, the
 * full text, the tags and the evidence all belong to the assessment workspace,
 * and duplicating them here would be a second, worse copy of that screen.
 *
 * The fetch is per family and cached by the hook once opened, so closing and
 * reopening does not re-request.
 */
function FamilyRow({ family }: { family: ComplianceFamily }) {
  const [open, setOpen] = useState(false);
  /* Null path until first opened — nothing is requested for a family nobody
     has looked at, which is the point of not shipping these with the
     catalogue. */
  const requirements = useResource<ComplianceRequirementName[]>(
    open ? `/compliance/families/${family.FamilyId}/requirements` : null,
  );
  const rows = requirements.data ?? [];

  return (
    <>
      <tr className="family-row" onClick={() => setOpen((v) => !v)}>
        <td className="mono xs">{family.Code}</td>
        <td>
          <div className="strong">{family.Title}</div>
          {family.Description && <div className="xs muted mt-2">{family.Description}</div>}
        </td>
        <td className="num">{family.RequirementCount}</td>
        <td className="xs muted">{open ? '▲' : '▼'}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={4} className="family-requirements">
            {/* An inner panel rather than styling the cell: a table cell cannot
                clip a border-radius, so rounding has to happen on a box inside
                it. */}
            <div className="requirement-panel">
              {requirements.loading ? (
                <Loading label="Loading requirements" />
              ) : requirements.error ? (
                <ErrorBanner message={requirements.error} />
              ) : rows.length === 0 ? (
                <p className="xs muted">This family has no requirements recorded.</p>
              ) : (
                <ol className="requirement-names">
                  {rows.map((r) => (
                    <li key={r.RequirementId}>
                      <span className="mono xs muted">{r.Code}</span>
                      <span className="small">{r.Title}</span>
                      {/* Only worth saying when it is not in every level. */}
                      {r.Applies !== 'all' && (
                        <span className="xs subtle">{r.Applies.split(',').join(' · ')}</span>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* =========================================================================
   Subjects

   The assessments list answers "what has been scored". This answers "who have
   we scored it for" — the same rows, grouped by the organisation they are
   about, which is the way an MSP actually thinks about compliance work.
   ========================================================================= */

/** Rolls a subject's assessments into the figures worth seeing in a list. */
function tallyFor(assessments: AssessmentSummaryRow[]) {
  const inScope = assessments.reduce((n, a) => n + a.InScope, 0);
  const assessed = assessments.reduce((n, a) => n + (a.InScope - a.NotAssessed), 0);
  const applicable = assessments.reduce((n, a) => n + a.Applicable, 0);
  const met = assessments.reduce((n, a) => n + a.Met, 0);
  return {
    started: assessments.length,
    complete: assessments.filter((a) => a.Status === 'complete').length,
    inScope,
    assessed,
    /* Weighted across every requirement rather than averaging the per-assessment
       percentages — a 4-requirement framework should not pull as hard as a
       153-requirement one. Null when nothing is applicable, which is not zero. */
    scorePct: applicable > 0 ? Math.round((met / applicable) * 100) : null,
  };
}

function ClientList({
  subjects,
  assessments,
  loading,
  onOpen,
}: {
  subjects: ComplianceSubject[];
  assessments: AssessmentSummaryRow[];
  loading: boolean;
  onOpen: (subject: number | typeof OURSELVES) => void;
}) {
  if (loading) return <Loading label="Loading clients" />;

  const ourOwn = assessments.filter((a) => a.ClientId === null);

  const rows = [
    /* Our own organisation is a subject like any other and usually the one with
       the most work against it. Leaving it out would make this list look empty
       while five assessments sat one tab away. */
    ...(ourOwn.length > 0
      ? [{ key: OURSELVES as typeof OURSELVES, name: 'Our organisation', type: null, items: ourOwn }]
      : []),
    ...subjects.map((s) => ({
      key: s.ClientId,
      name: s.Name,
      type: s.Status,
      items: assessments.filter((a) => a.ClientId === s.ClientId),
    })),
  ];

  if (rows.length === 0) {
    return (
      <Empty title="Nobody to show">
        <p>
          The companies here are the estate's shared client list — the same rows Account
          Management and Sales work from. Add one from the start-an-assessment dialog and it
          appears in all three.
        </p>
      </Empty>
    );
  }

  return (
    <div className="card">
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Who</th>
              <th style={{ width: 110 }}>Relationship</th>
              <th className="num" style={{ width: 100 }}>Assessments</th>
              <th className="num" style={{ width: 110 }}>Requirements</th>
              <th style={{ width: 170 }}>Assessed</th>
              <th className="num" style={{ width: 80 }}>Met</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const t = tallyFor(row.items);
              const pct = t.inScope > 0 ? Math.round((t.assessed / t.inScope) * 100) : 0;
              return (
                <tr key={String(row.key)}>
                  <td className="strong">{row.name}</td>
                  <td>
                    {row.type === null ? (
                      <span className="badge badge-neutral">Ourselves</span>
                    ) : (
                      <span className="xs">{humanise(row.type)}</span>
                    )}
                  </td>
                  <td className="num">
                    {t.started === 0 ? (
                      <span className="subtle">none</span>
                    ) : (
                      <>
                        {t.started}
                        {t.complete > 0 && <div className="xs muted">{t.complete} complete</div>}
                      </>
                    )}
                  </td>
                  <td className="num">{t.inScope || <span className="subtle">—</span>}</td>
                  <td>
                    {t.started === 0 ? (
                      <span className="xs subtle">nothing started</span>
                    ) : (
                      <>
                        <span className="meter">
                          <span className={pct < 50 ? 'low' : ''} style={{ width: `${pct}%` }} />
                        </span>
                        <div className="xs muted mt-2">
                          {t.assessed} of {t.inScope}
                        </div>
                      </>
                    )}
                  </td>
                  <td className="num mono">{t.scorePct === null ? '—' : `${t.scorePct}%`}</td>
                  <td className="actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => onOpen(row.key)}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ClientDetail({
  clientId,
  name,
  subjectStatus,
  assessments,
  onBack,
  onOpenAssessment,
  onStart,
  onSaved,
  onError,
}: {
  /** The subject whose adoption this page reads and writes. Null = ourselves. */
  clientId: number | null;
  name: string;
  subjectStatus: string | null;
  assessments: AssessmentSummaryRow[];
  onBack: () => void;
  onOpenAssessment: (id: number) => void;
  onStart: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const t = tallyFor(assessments);
  /* THIS subject's adoption, not the organisation's. The catalogue endpoint
     takes the subject and returns their row per framework, defaulting to
     "unreviewed" where they have not decided anything yet. */
  const adoption = useResource<ComplianceFramework[]>(
    `/compliance/frameworks${clientId === null ? '' : `?clientId=${clientId}`}`,
  );
  const frameworkFor = (id: number) =>
    (adoption.data ?? []).find((f) => f.FrameworkId === id) ?? null;
  const [saving, setSaving] = useState<number | null>(null);

  const save = async (frameworkId: number, patch: Record<string, unknown>) => {
    setSaving(frameworkId);
    try {
      /* clientId travels with every write, so the row created or updated is
         this subject's. Partial body — the endpoint COALESCEs the enums, so
         one field cannot blank the others. */
      const updated = await api.put<ComplianceFramework>(
        `/compliance/frameworks/${frameworkId}`,
        { ...patch, clientId },
      );
      adoption.set((cur) =>
        cur.map((f) => (f.FrameworkId === frameworkId ? { ...f, ...updated } : f)),
      );
      onSaved();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setSaving(null);
    }
  };

  return (
    <>
      <button type="button" className="btn btn-ghost btn-sm mb-4" onClick={onBack}>
        ← All clients
      </button>

      <PageHead
        title={name}
        lede={
          t.started === 0
            ? 'No assessment has been started against this organisation yet.'
            : `${t.started} ${t.started === 1 ? 'assessment' : 'assessments'} · ${t.assessed} of ${t.inScope} requirements assessed`
        }
        actions={
          <>
            {subjectStatus && <span className="badge badge-neutral">{humanise(subjectStatus)}</span>}
            <button type="button" className="btn btn-primary" onClick={onStart}>
              Start an assessment
            </button>
          </>
        }
      />

      {assessments.length === 0 ? (
        <Empty title="Nothing started">
          <p>
            An assessment scores every in-scope requirement of one framework against this
            organisation. Start one and it will show up here.
          </p>
          <button type="button" className="btn btn-primary mt-4" onClick={onStart}>
            Start the first one
          </button>
        </Empty>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Framework</th>
                  <th style={{ width: 180 }}>Scope</th>
                  <th style={{ width: 150 }}>Does it apply?</th>
                  <th style={{ width: 130 }}>Where we are</th>
                  <th style={{ width: 160 }}>Progress</th>
                  <th className="num" style={{ width: 80 }}>Met</th>
                  <th style={{ width: 120 }}>Started</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {assessments.map((a) => {
                  const done = a.InScope - a.NotAssessed;
                  const pct = a.InScope > 0 ? Math.round((done / a.InScope) * 100) : 0;
                  const f = frameworkFor(a.FrameworkId);
                  return (
                    <tr key={a.AssessmentId}>
                      <td>
                        <div className="strong">{a.FrameworkShortName ?? a.FrameworkName}</div>
                        <div className="xs muted">{humanise(a.Category)}</div>
                      </td>
                      <td className="xs">{a.ScopeLevelLabel ?? a.ScopeLevelKey}</td>
                      <td>
                        {f ? (
                          <select
                            className="select"
                            value={f.Applicability}
                            disabled={saving === f.FrameworkId}
                            aria-label={`Does ${f.ShortName ?? f.Name} apply to ${name}`}
                            onChange={(e) =>
                              void save(f.FrameworkId, { applicability: e.target.value })
                            }
                          >
                            <option value="unreviewed">Not yet decided</option>
                            <option value="required">Required</option>
                            <option value="recommended">Recommended</option>
                            <option value="considering">Considering</option>
                            <option value="not_applicable">Not applicable</option>
                          </select>
                        ) : (
                          <span className="xs subtle">—</span>
                        )}
                      </td>
                      <td>
                        {f ? (
                          <select
                            className="select"
                            value={f.Status}
                            disabled={saving === f.FrameworkId}
                            aria-label={`Where ${name} is on ${f.ShortName ?? f.Name}`}
                            onChange={(e) => void save(f.FrameworkId, { status: e.target.value })}
                          >
                            <option value="not_started">Not started</option>
                            <option value="assessing">Assessing</option>
                            <option value="remediating">Remediating</option>
                            <option value="maintained">Maintained</option>
                          </select>
                        ) : (
                          <span className="xs subtle">—</span>
                        )}
                      </td>
                      <td>
                        <span className="meter">
                          <span className={pct < 50 ? 'low' : ''} style={{ width: `${pct}%` }} />
                        </span>
                        <div className="xs muted mt-2">
                          {done} of {a.InScope}
                        </div>
                      </td>
                      <td className="num mono">
                        {a.ScorePct === null ? '—' : `${a.ScorePct}%`}
                      </td>
                      <td className="xs mono">{formatDate(a.StartedOn)}</td>
                      <td className="actions">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => onOpenAssessment(a.AssessmentId)}
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="card-body tight">
            <p className="xs muted">
              “Does it apply?” and “Where we are” are recorded against{' '}
              <strong>{name}</strong> alone. Another client can answer the same framework
              differently.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

function AssessmentList({
  rows,
  loading,
  onOpen,
  onStart,
  onDelete,
}: {
  rows: AssessmentSummaryRow[];
  loading: boolean;
  onOpen: (id: number) => void;
  onStart: () => void;
  onDelete: (id: number) => void;
}) {
  if (loading) return <Loading label="Loading assessments" />;

  if (rows.length === 0) {
    return (
      <Empty title="Nothing assessed yet">
        <p>
          An assessment scores every in-scope requirement of one framework against one subject —
          us, or a client. Pick the framework and the scope level, then work the list down.
        </p>
        <button type="button" className="btn btn-primary mt-4" onClick={onStart}>
          Start the first one
        </button>
      </Empty>
    );
  }

  return (
    <div className="card">
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Subject</th>
              <th>Framework</th>
              <th style={{ width: 190 }}>Scope</th>
              <th className="num" style={{ width: 80 }}>Met</th>
              <th style={{ width: 170 }}>Assessed</th>
              <th style={{ width: 130 }}>Owner</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => {
              const done = a.InScope - a.NotAssessed;
              const pct = a.InScope > 0 ? Math.round((done / a.InScope) * 100) : 0;
              return (
                <tr key={a.AssessmentId}>
                  <td className="strong">{a.ClientName ?? 'Our organisation'}</td>
                  <td>
                    {a.FrameworkShortName ?? a.FrameworkName}
                    <div className="xs muted">{humanise(a.Category)}</div>
                  </td>
                  <td className="xs muted">{a.ScopeLevelLabel ?? a.ScopeLevelKey}</td>
                  <td className="num mono strong">
                    {a.ScorePct === null ? '—' : `${a.ScorePct}%`}
                  </td>
                  <td>
                    <div className="row" style={{ gap: 'var(--space-2)' }}>
                      <span className="meter grow">
                        <span className={pct < 50 ? 'low' : ''} style={{ width: `${pct}%` }} />
                      </span>
                      <span className="mono xs muted">
                        {done}/{a.InScope}
                      </span>
                    </div>
                  </td>
                  <td className="xs">{a.OwnerName ?? <span className="subtle">—</span>}</td>
                  <td className="actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => onOpen(a.AssessmentId)}
                    >
                      Open
                    </button>
                    <DeleteButton
                      what={`the ${a.FrameworkShortName ?? a.FrameworkName} assessment for ${a.ClientName ?? 'our organisation'}, and its scoring`}
                      onConfirm={() => onDelete(a.AssessmentId)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewAssessment({
  frameworks,
  subjects,
  employees,
  preselect,
  existing,
  onClose,
  onCreated,
  onFrameworkSaved,
  onOpenExisting,
}: {
  frameworks: ComplianceFramework[];
  subjects: ComplianceSubject[];
  employees: Employee[];
  preselect: ComplianceFramework | null;
  existing: AssessmentSummaryRow[];
  onClose: () => void;
  onCreated: (assessmentId: number) => void;
  onFrameworkSaved: (id: number, patch: Partial<ComplianceFramework>) => void;
  onOpenExisting: (assessmentId: number) => void;
}) {
  const [frameworkId, setFrameworkId] = useState<number | null>(
    preselect?.FrameworkId ?? frameworks[0]?.FrameworkId ?? null,
  );
  /* '' is our own organisation, not "nothing chosen" — an assessment always
     has a subject, and us is the default one. NEW means "one that does not
     exist yet", handled below. */
  const NEW = '__new__';
  const [clientId, setClientId] = useState<string>('');
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<'prospect' | 'client'>('client');
  const [scopeLevelKey, setScopeLevelKey] = useState<string | null>(null);
  const [ownerEmployeeId, setOwnerEmployeeId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const framework = frameworks.find((f) => f.FrameworkId === frameworkId) ?? null;
  const levels = framework?.levels ?? [];
  const level = scopeLevelKey ?? levels[0]?.LevelKey ?? 'all';
  const creatingNew = clientId === NEW;

  /* A company recorded twice is the failure this estate is built to avoid, so
     an exact name match blocks the create and points at the existing row
     rather than silently making a second one. */
  const duplicate = creatingNew
    ? (subjects.find((s) => s.Name.trim().toLowerCase() === newName.trim().toLowerCase()) ?? null)
    : null;

  const clash = creatingNew
    ? undefined
    : existing.find(
        (a) =>
          a.FrameworkId === frameworkId &&
          (clientId === '' ? a.ClientId === null : a.ClientId === Number(clientId)),
      );

  const canStart =
    frameworkId !== null && (!creatingNew || (newName.trim().length > 0 && duplicate === null));

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      /* Creating the company first, through the Sales endpoint rather than a
         compliance-only one: there is a single list of who we deal with, and a
         company added here is the same row Sales and Account Management see.
         What comes back and is used here is its ClientId — the canonical row —
         not the sales account wrapped around it. */
      let subjectId = clientId === '' ? null : Number(clientId);
      if (creatingNew) {
        const account = await api.post<{ ClientId: number }>('/sales/accounts', {
          name: newName.trim(),
          accountType: newType,
        });
        subjectId = account.ClientId;
      }

      const created = await api.post<{ AssessmentId: number }>('/compliance/assessments', {
        frameworkId,
        clientId: subjectId,
        scopeLevelKey: level,
        ownerEmployeeId,
      });
      onCreated(created.AssessmentId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start it');
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Start an assessment"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          {clash ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onOpenExisting(clash.AssessmentId)}
            >
              Open the existing one
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving || !canStart}
              onClick={() => void submit()}
            >
              {saving ? 'Starting…' : creatingNew ? 'Add and start' : 'Start'}
            </button>
          )}
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <Field label="Who is being assessed">
        <select className="select" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">Our organisation</option>
          {/* Clients first, then prospects — the order the endpoint returns
              them in. Both are offered: an assessment is often how a prospect
              is won, so ruling them out would remove the sales case for having
              this page at all. */}
          {subjects.map((sub) => (
            <option key={sub.ClientId} value={String(sub.ClientId)}>
              {sub.Name}
              {sub.Status === 'active' ? '' : ` (${humanise(sub.Status)})`}
              {sub.Assessments > 0 ? ` — ${sub.Assessments} already` : ''}
            </option>
          ))}
          <option value={NEW}>+ A new organisation…</option>
        </select>
      </Field>

      {creatingNew && (
        <div className="form-grid">
          <Field label="Organisation name">
            <input
              className="input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Ridgeline Dental"
              autoFocus
            />
          </Field>
          <Field label="Relationship">
            <select
              className="select"
              value={newType}
              onChange={(e) => setNewType(e.target.value as typeof newType)}
            >
              <option value="client">Client</option>
              <option value="prospect">Prospect</option>
            </select>
          </Field>
        </div>
      )}

      {duplicate && (
        <ErrorBanner
          message={`“${duplicate.Name}” is already on the list — choose it above rather than adding a second copy.`}
        />
      )}

      {creatingNew && !duplicate && (
        <p className="xs muted">
          This adds the organisation to the shared company list, so it shows up in Sales too.
          Everything else about it — contacts, seats, account manager — can be filled in there.
        </p>
      )}

      {subjects.length === 0 && !creatingNew && (
        <p className="xs muted">
          The only companies listed are the ones Sales knows about. Choose “A new organisation” to
          add one without leaving this page.
        </p>
      )}

      <Field label="Framework">
        <select
          className="select"
          value={frameworkId ?? ''}
          onChange={(e) => {
            setFrameworkId(Number(e.target.value));
            setScopeLevelKey(null);
          }}
        >
          {frameworks.map((f) => (
            <option key={f.FrameworkId} value={f.FrameworkId}>
              {f.Name}
              {/* Marked in the list itself, so the undecided ones are visible
                  before one is picked rather than only after. */}
              {f.Applicability === 'unreviewed' ? ' — not yet decided' : ''}
            </option>
          ))}
        </select>
      </Field>

      {/* Hidden while adding a new organisation: it has no id yet, so there is
          nothing to record a decision against until it is created. */}
      {framework && !creatingNew && (
        <FrameworkDecision
          framework={framework}
          clientId={clientId === '' ? null : Number(clientId)}
          subjectName={
            clientId === ''
              ? 'us'
              : (subjects.find((s) => s.ClientId === Number(clientId))?.Name ?? 'this client')
          }
          undecidedCount={
            frameworks.filter(
              (f) => f.Applicability === 'unreviewed' && f.FrameworkId !== framework.FrameworkId,
            ).length
          }
          employees={employees}
          onSaved={onFrameworkSaved}
        />
      )}

      {/* Only CIS has real levels; showing a one-option select everywhere else
          would be a control that does nothing. */}
      {levels.length > 1 && (
        <Field
          label="Scope"
          hint="The level sets the denominator — an IG1 assessment is scored against the IG1 safeguards only."
        >
          <select
            className="select"
            value={level}
            onChange={(e) => setScopeLevelKey(e.target.value)}
          >
            {levels.map((l) => (
              <option key={l.LevelKey} value={l.LevelKey}>
                {l.Label}
              </option>
            ))}
          </select>
        </Field>
      )}

      <Field label="Owner">
        <EmployeePicker
          employees={employees}
          value={ownerEmployeeId}
          onChange={setOwnerEmployeeId}
          placeholder="Nobody named"
        />
      </Field>

      {framework && (
        <p className="xs muted">
          {framework.RequirementCount} requirements across {framework.FamilyCount} families.
          {clash && ' There is already an assessment for this subject and framework.'}
        </p>
      )}
    </Modal>
  );
}

/* =========================================================================
   Recording what we decided
   ========================================================================= */

/* =========================================================================
   Triage

   The four figures at the top of this page — in scope, maintained, unowned,
   reviews overdue — are all read from adoption columns on the framework:
   Applicability, Status, OwnerEmployeeId and NextReviewDate. They are real
   columns, but they are all still at their seeded defaults, so every figure
   reads zero and the page looks like it has no data behind it.

   The fields were already editable, one framework at a time, four clicks deep:
   pick a framework, open its detail, press Edit, fill the modal. Nobody is
   going to do that ten times, which is precisely why it had never been done.
   This panel puts the same four fields inline, so the whole catalogue can be
   triaged in one pass and the figures above become true.
   ========================================================================= */

/** What is missing on a framework before it can count toward the figures. */
function gapsOn(f: ComplianceFramework, today: string): string[] {
  if (f.Applicability === 'unreviewed') return ['undecided'];
  /* Only in-scope frameworks can be unowned or overdue — the other states are
     finished answers and asking for an owner on them would be noise. */
  if (f.Applicability !== 'required' && f.Applicability !== 'recommended') return [];
  const gaps: string[] = [];
  if (f.OwnerEmployeeId === null) gaps.push('unowned');
  if (!f.NextReviewDate) gaps.push('no review date');
  else if (f.NextReviewDate.slice(0, 10) < today) gaps.push('review overdue');
  return gaps;
}

/**
 * Decide the framework you are about to assess, without leaving the dialog.
 *
 * Sits directly under the framework picker, because choosing a framework to
 * assess is the moment you already have an opinion on whether it applies —
 * and an assessment against a framework nobody has ruled in is how the four
 * figures on this page end up at zero while the work is visibly happening.
 *
 * Deliberately one framework at a time rather than the whole catalogue: a
 * ten-row grid inside a dialog is a second page pretending to be a field.
 */
function FrameworkDecision({
  framework: catalogueRow,
  clientId,
  subjectName,
  undecidedCount,
  employees,
  onSaved,
}: {
  framework: ComplianceFramework;
  /** Whose decision this is. Null = ourselves. */
  clientId: number | null;
  subjectName: string;
  undecidedCount: number;
  employees: Employee[];
  onSaved: (id: number, patch: Partial<ComplianceFramework>) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* The dialog was handed the ORGANISATION's catalogue. Once a subject is
     chosen the answer being edited is theirs, so it is fetched for them —
     otherwise this box would show our position and save it against a client. */
  const forSubject = useResource<ComplianceFramework[]>(
    `/compliance/frameworks${clientId === null ? '' : `?clientId=${clientId}`}`,
  );
  const f =
    (forSubject.data ?? []).find((x) => x.FrameworkId === catalogueRow.FrameworkId) ?? catalogueRow;

  const gaps = gapsOn(f, today);
  const inScope = f.Applicability === 'required' || f.Applicability === 'recommended';

  const save = async (patch: Record<string, unknown>) => {
    setSaving(true);
    setError(null);
    try {
      /* Partial body: the endpoint COALESCEs the enums and gates the nullables
         behind "was this key sent", so one field cannot blank the others. */
      const updated = await api.put<ComplianceFramework>(
        `/compliance/frameworks/${f.FrameworkId}`,
        { ...patch, clientId },
      );
      onSaved(f.FrameworkId, updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="decision-box">
      {error && <ErrorBanner message={error} />}

      <div className="row-between wrap">
        <span className="eyebrow">Decided for {subjectName}</span>
        {forSubject.loading ? (
          <span className="xs muted">loading…</span>
        ) : gaps.length === 0 ? (
          <span className="badge badge-good">Decided</span>
        ) : (
          <span className="xs muted">{gaps.join(' · ')}</span>
        )}
      </div>

      <div className="form-grid mt-2">
        <Field label={`Does it apply to ${subjectName}?`}>
          <select
            className="select"
            value={f.Applicability}
            disabled={saving}
            onChange={(e) => void save({ applicability: e.target.value })}
          >
            <option value="unreviewed">Not yet decided</option>
            <option value="required">Required</option>
            <option value="recommended">Recommended</option>
            <option value="considering">Considering</option>
            <option value="not_applicable">Not applicable</option>
          </select>
        </Field>

        {/* The rest only means anything once somebody has said the work is
            ours — asking who owns a framework we have ruled out is noise. */}
        {inScope && (
          <>
            <Field label="Where we are">
              <select
                className="select"
                value={f.Status}
                disabled={saving}
                onChange={(e) => void save({ status: e.target.value })}
              >
                <option value="not_started">Not started</option>
                <option value="assessing">Assessing</option>
                <option value="remediating">Remediating</option>
                <option value="maintained">Maintained</option>
              </select>
            </Field>
            <Field label="Owner">
              <EmployeePicker
                employees={employees}
                value={f.OwnerEmployeeId}
                placeholder="Nobody named"
                onChange={(ownerEmployeeId) => void save({ ownerEmployeeId })}
              />
            </Field>
            <Field label="Next review">
              <input
                className="input"
                type="date"
                value={f.NextReviewDate?.slice(0, 10) ?? ''}
                disabled={saving}
                onChange={(e) => void save({ nextReviewDate: e.target.value || null })}
              />
            </Field>
          </>
        )}
      </div>

      {undecidedCount > 0 && (
        <p className="xs muted mt-2">
          {undecidedCount} other {undecidedCount === 1 ? 'framework has' : 'frameworks have'} never
          been ruled in or out for us. They are marked in the list above.
        </p>
      )}
    </div>
  );
}

